import AngryEmoji from "@/assets/svg/Angry_emoji.svg?react";
import Buzzsumo from "@/assets/svg/Buzzsumo.svg?react";
import FacebookIcon from "@/assets/svg/Facebook_icon.svg?react";
import HahaEmoji from "@/assets/svg/Haha_emoji.svg?react";
import LoveEmoji from "@/assets/svg/Love_emoji.svg?react";
import PinterestIcon from "@/assets/svg/Pinterest icon.svg?react";
import RedditIcon from "@/assets/svg/Reddit_icon.svg?react";
import SadEmoji from "@/assets/svg/Sad_emoji.svg?react";
import WowEmoji from "@/assets/svg/Wow_emoji.svg?react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/data-display/tooltip";
import {
  ArrowFatUpIcon,
  ChatIcon,
  ShareFatIcon,
  ThumbsUpIcon,
  UploadSimpleIcon,
  XLogoIcon,
} from "@phosphor-icons/react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { formatNumber } from "@/components/ui/article-card/utils";
import { Skeleton } from "@/components/ui/feedback/skeleton";

/**
 * Normalized social metrics interface that works with both
 * news feed data and discovery view data
 */
export interface SocialMetrics {
  facebookComments: number | null;
  facebookReactions: number | null;
  facebookShares: number | null;
  facebookLikes: number | null;
  redditEngagements: number | null;
  twitterShares: number | null;
  hahaCount: number | null;
  loveCount: number | null;
  sadCount: number | null;
  wowCount: number | null;
  angryCount: number | null;
  pinterestShares: number | null;
  // Optional fields that may only exist in discovery view
  alexaRank?: number | null;
  numLinkingDomains?: number | null;
}

interface SocialEngagementTooltipProps {
  /**
   * Normalized social metrics data
   */
  socialMetrics: SocialMetrics | null;
  /**
   * Total social engagement score to display
   */
  score?: number | null;
  /**
   * Loading state for the tooltip content
   */
  loading: boolean;
  /**
   * Callback when hover state changes (for lazy loading)
   */
  onHoverChange?: (isHovering: boolean) => void;
}

interface SocialDataItemProps {
  icon: React.ElementType;
  label: string;
  value: number | null;
  loading: boolean;
}

const SocialDataItem: React.FC<SocialDataItemProps> = ({
  icon: Icon,
  label,
  value,
  loading,
}) => (
  <div className="flex justify-between items-center text-xs text-gray-500 mt-1 pl-5">
    <div className="flex items-center space-x-2">
      <Icon className="w-4 h-4" />
      <span>{label}:</span>
    </div>
    <p className="font-bold text-gray-500">
      {loading ? (
        <Skeleton className="h-3 w-5 bg-gray-500/25" />
      ) : value !== null ? (
        formatNumber(value)
      ) : (
        0
      )}
    </p>
  </div>
);

interface SocialSectionProps {
  title: string;
  icon: React.ElementType;
  items: Array<{
    label: string;
    value?: number | null;
    icon: React.ElementType;
  }>;
  loading: boolean;
}

const SocialSection: React.FC<SocialSectionProps> = ({
  title,
  icon: SectionIcon,
  items,
  loading,
}) => (
  <div className="mb-2">
    <h3 className="font-normal flex items-center space-x-2 text-xs text-gray-500">
      <div className="flex items-center space-x-2">
        <SectionIcon className="w-4 h-4" />
        <span>{title}</span>
      </div>
    </h3>
    {items.map(({ label, value, icon }) => (
      <SocialDataItem
        key={label}
        icon={icon}
        label={label}
        value={value ?? 0}
        loading={loading}
      />
    ))}
  </div>
);

/**
 * Tooltip content component that displays detailed social media metrics.
 * Can be used independently with a custom trigger.
 */
export const SocialEngagementTooltipContent: React.FC<{
  socialMetrics: SocialMetrics | null;
  loading: boolean;
}> = ({ socialMetrics, loading }) => {
  return (
    <>
      <SocialSection
        title="Facebook engagement"
        icon={FacebookIcon}
        items={[
          {
            label: "Likes",
            value:
              socialMetrics?.facebookLikes || socialMetrics?.facebookReactions,
            icon: ThumbsUpIcon,
          },
          {
            label: "Shares",
            value: socialMetrics?.facebookShares,
            icon: ShareFatIcon,
          },
          {
            label: "Comments",
            value: socialMetrics?.facebookComments,
            icon: ChatIcon,
          },
        ]}
        loading={loading}
      />
      <SocialSection
        title="Facebook reactions"
        icon={FacebookIcon}
        items={[
          {
            label: "Angry",
            value: socialMetrics?.angryCount,
            icon: AngryEmoji,
          },
          {
            label: "Sad",
            value: socialMetrics?.sadCount,
            icon: SadEmoji,
          },
          {
            label: "Haha",
            value: socialMetrics?.hahaCount,
            icon: HahaEmoji,
          },
          {
            label: "Wow",
            value: socialMetrics?.wowCount,
            icon: WowEmoji,
          },
          {
            label: "Love",
            value: socialMetrics?.loveCount,
            icon: LoveEmoji,
          },
        ]}
        loading={loading}
      />
      <SocialSection
        title="X (Twitter) engagement"
        icon={XLogoIcon}
        items={[
          {
            label: "Shares",
            value: socialMetrics?.twitterShares,
            icon: ShareFatIcon,
          },
        ]}
        loading={loading}
      />
      <SocialSection
        title="Reddit engagement"
        icon={RedditIcon}
        items={[
          {
            label: "Votes and comments",
            value: socialMetrics?.redditEngagements,
            icon: ArrowFatUpIcon,
          },
        ]}
        loading={loading}
      />
      <SocialSection
        title="Pinterest engagement"
        icon={PinterestIcon}
        items={[
          {
            label: "Shares",
            value: socialMetrics?.pinterestShares,
            icon: UploadSimpleIcon,
          },
        ]}
        loading={loading}
      />
    </>
  );
};

/**
 * Social engagement tooltip component that displays detailed social media metrics
 * for Facebook, Twitter/X, Reddit, and Pinterest engagements.
 *
 * Supports both news feed data and discovery view data through a normalized interface.
 */
const SocialEngagementTooltip: React.FC<SocialEngagementTooltipProps> = ({
  socialMetrics,
  score,
  loading,
  onHoverChange,
}) => {
  const handleMouseEnter = () => {
    onHoverChange?.(true);
  };

  const handleMouseLeave = () => {
    onHoverChange?.(false);
  };

  return (
    <TooltipProvider>
      <div className="inline-flex space-x-1 items-center text-gray-500 text-xs">
        <Tooltip>
          <TooltipTrigger className="inline-flex space-x-1 items-center text-gray-500 text-xs">
            <Buzzsumo className="w-4 h-4 rounded-full" />
          </TooltipTrigger>
          <TooltipContent className="max-w-80 text-xs">
            Social engagements for Facebook, Twitter/X, and Pinterest
          </TooltipContent>
        </Tooltip>
        {score !== undefined && score !== null ? (
          <Tooltip>
            <TooltipTrigger>
              <span
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className="inline-flex space-x-1 items-center text-gray-500 text-xs cursor-pointer"
              >
                <span>Social: </span>
                <p className="font-bold text-gray-500">{formatNumber(score)}</p>
              </span>
            </TooltipTrigger>
            <TooltipPrimitive.Portal>
              <TooltipContent className="p-4 w-64 bg-white shadow-md rounded-lg z-50 relative overflow-visible">
                <SocialEngagementTooltipContent
                  socialMetrics={socialMetrics}
                  loading={loading}
                />
              </TooltipContent>
            </TooltipPrimitive.Portal>
          </Tooltip>
        ) : (
          <>
            <span>Social: </span>
            <p className="font-bold text-gray-500">N/A</p>
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

export default SocialEngagementTooltip;
